'use client';

import {
  getClientApplicationConfig,
  getClientApplicationContext,
} from '@hc-frontend/core-context';
import { useEventBus, useTrackingContexts } from '@hc-frontend/core-ui';
import type {
  TrackClickUseCase,
  TrackConversionUseCase,
  TrackImpressionUseCase,
} from '@hc-frontend/features-ads';
import {
  conversionManagerAdapterFactory,
  semContentAdapterFactory,
  trackClickFactory,
  trackConversionFactory,
  trackImpressionFactory,
} from '@hc-frontend/features-ads';
import type {
  TrackSubmitPersonalInformationPlateUseCase,
  TrackSubmitZipPlateUseCase,
} from '@hc-frontend/features-tracking';
import {
  trackSubmitPersonalInformationPlateAdapterFactory,
  trackSubmitPersonalInformationPlateFactory,
  trackSubmitZipPlateAdapterFactory,
  trackSubmitZipPlateFactory,
} from '@hc-frontend/features-tracking';

export interface CompositionRoot {
  trackAdConversion: TrackConversionUseCase;
  trackAdImpression: TrackImpressionUseCase;
  trackAdClick: TrackClickUseCase;
  trackZipPlateSubmission: TrackSubmitZipPlateUseCase;
  trackSubmitPersonalInformationPlate: TrackSubmitPersonalInformationPlateUseCase;
}

let root: CompositionRoot;

export function useCompositionRoot() {
  const eventBus = useEventBus();
  const trackingContexts = useTrackingContexts();

  if (root) {
    return root;
  }

  const config = getClientApplicationConfig();
  const { log } = getClientApplicationContext(null);

  const conversionManagerRepository = conversionManagerAdapterFactory({
    baseUrl: config.conversionManager.baseUrl,
  });
  const trackAdConversion = trackConversionFactory(conversionManagerRepository);

  const semContentRepository = semContentAdapterFactory({
    baseUrl: config.sem.apiURL,
  });
  const trackAdImpression = trackImpressionFactory(semContentRepository);

  const trackAdClick = trackClickFactory(semContentRepository);

  const { trackSubmitZipPlate } = trackSubmitZipPlateAdapterFactory({
    eventBus,
    trackingContexts,
  });
  const trackZipPlateSubmission = trackSubmitZipPlateFactory({
    trackSubmitZipPlate,
    log,
  });

  const {
    trackSubmitPersonalInformationPlate: trackSubmitPersonalInformationAdapter,
  } = trackSubmitPersonalInformationPlateAdapterFactory({
    eventBus,
    trackingContexts,
  });
  const trackSubmitPersonalInformationPlate =
    trackSubmitPersonalInformationPlateFactory({
      trackSubmitPersonalInformationPlate:
        trackSubmitPersonalInformationAdapter,
      log,
    });

  root = {
    trackZipPlateSubmission,
    trackSubmitPersonalInformationPlate,
    trackAdConversion,
    trackAdImpression,
    trackAdClick,
  };

  return root;
}
